import React from "react";
import { useHistory, useParams, useLocation } from "react-router-dom"; // <-- Import useLocation
import { Helmet } from "react-helmet-async";

import { FieldServicesAppRoutes } from "../lib/constants";
import {
  PageRoot,
  PageAppBar,
  PageContainer,
  ErrorCard,
  ContentSkeleton,
} from "../components/ui";
import { useSelectedWell } from "../hooks/useSelectedWell";
import { DynamicForm } from "../forms/DynamicForm";
import { wellRecordFormConfig } from "../formConfigs/wellRecordFormConfig";
import { useDynamicForm } from "../hooks/useDynamicForm";

const WellRecordPage = () => {
  const history = useHistory();
  const location = useLocation(); // <-- Get current location
  const { id } = useParams();
  const isNewRecord = id === "new";

  const { data, isFetching, error, refetch } = useSelectedWell(
    isNewRecord ? null : id
  );

  const queryParams = new URLSearchParams(location.search);
  const redirectPath = queryParams.get("redirect");

  // This is where we get our NDX from existing well OR undefined
  const wellNdx = !isNewRecord ? data?.well_ndx : null;

  // Handle redirect using query param if available
  const handleRedirect = () => {
    history.push(redirectPath || FieldServicesAppRoutes.WELL_ACTIONS(id));
  };

  const {
    isLoading: isLoadingFormData,
    error: formDataError,
    refetchFormData,
    refetchLookups,
    ...rest
  } = useDynamicForm({
    ndx: wellNdx,
    isNewRecord,
    config: wellRecordFormConfig,
    endpoints: {
      fetch: "/well-actions/well-record",
      submit: "/well-actions/well-record",
    },
    handleRedirect,
  });

  const anyError = error || formDataError;
  const loading = isFetching || isLoadingFormData;

  return (
    <PageRoot>
      <Helmet title="Well Record | Field Services" />
      <PageAppBar
        title={
          <>
            <strong>Well Record:</strong> {id}
          </>
        }
        buttonPath={
          redirectPath ||
          (isNewRecord
            ? FieldServicesAppRoutes.HOME()
            : FieldServicesAppRoutes.WELL_ACTIONS(id))
        }
        buttonLabel={
          redirectPath ? "Back" : isNewRecord ? "Home" : "Well Actions"
        }
      />
      <PageContainer>
        {anyError ? (
          <ErrorCard
            title={isNewRecord ? "Failed to Load" : `Failed to Load Well ${id}`}
            message={error?.message || formDataError?.message}
            onRetry={async () => {
              await Promise.all([
                refetch(),
                refetchLookups(),
                refetchFormData(),
              ]);
            }}
          />
        ) : loading ? (
          <ContentSkeleton />
        ) : (
          <DynamicForm config={wellRecordFormConfig} {...rest} />
        )}
      </PageContainer>
    </PageRoot>
  );
};

export default WellRecordPage;
